<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">Invoices</h1>
        <div class="gl-breadcrumb">
          <router-link to="/" class="gl-breadcrumb-link">Home</router-link>
          <span class="gl-breadcrumb-sep">/</span>
          <span class="gl-breadcrumb-link">Super Admin</span>
          <span class="gl-breadcrumb-sep">/</span>
          <span>Invoices</span>
        </div>
      </div>
    </div>

    <!-- Stats skeleton -->
    <div v-if="loading" class="gl-stats-grid">
      <div v-for="i in 4" :key="i" class="gl-stat-card">
        <div class="gl-skel" style="width:40px;height:40px;border-radius:10px;flex-shrink:0"></div>
        <div style="flex:1">
          <div class="gl-skel" style="height:10px;width:60%;border-radius:6px;margin-bottom:7px"></div>
          <div class="gl-skel" style="height:22px;width:45%;border-radius:6px"></div>
        </div>
      </div>
    </div>

    <!-- Stats -->
    <div v-if="!loading && stats" class="gl-stats-grid">
      <div class="gl-stat-card">
        <div class="gl-stat-icon gl-icon-green">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
        </div>
        <div>
          <div class="gl-stat-label">Total Revenue</div>
          <div class="gl-stat-value">RM {{ formatCurrency(stats.total_revenue) }}</div>
          <div class="gl-stat-sub">all time</div>
        </div>
      </div>
      <div class="gl-stat-card">
        <div class="gl-stat-icon gl-icon-teal">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        </div>
        <div>
          <div class="gl-stat-label">This Month</div>
          <div class="gl-stat-value">RM {{ formatCurrency(stats.this_month) }}</div>
          <div class="gl-stat-sub">collected</div>
        </div>
      </div>
      <div class="gl-stat-card">
        <div class="gl-stat-icon gl-icon-amber">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </div>
        <div>
          <div class="gl-stat-label">Pending</div>
          <div class="gl-stat-value">{{ stats.pending_count || 0 }}</div>
          <div class="gl-stat-sub">awaiting payment</div>
        </div>
      </div>
      <div class="gl-stat-card">
        <div class="gl-stat-icon gl-icon-blue">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
        </div>
        <div>
          <div class="gl-stat-label">Total Invoices</div>
          <div class="gl-stat-value">{{ stats.total_count || 0 }}</div>
          <div class="gl-stat-sub">all time</div>
        </div>
      </div>
    </div>

    <!-- Filter bar -->
    <div class="gl-filter-bar">
      <div class="gl-search-wrap">
        <svg class="gl-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input type="text" class="gl-search-input" placeholder="Search company…" v-model="search" @input="debouncedSearch" />
      </div>
      <div class="gl-select-wrap">
        <select v-model="filters.status" @change="fetchInvoices" class="gl-select">
          <option value="">All Status</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="overdue">Overdue</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
    </div>

    <!-- Table card -->
    <div class="gl-card">
      <!-- Skeleton -->
      <template v-if="loading">
        <div class="gl-tbl-skel">
          <div class="gl-tbl-skel-head">
            <div class="gl-skel" style="height:11px;width:90px;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;flex:1.2;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:80px;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:80px;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:60px;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:80px;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:80px;border-radius:6px"></div>
            <div class="gl-skel" style="height:11px;width:64px;border-radius:6px"></div>
          </div>
          <div v-for="i in 7" :key="i" class="gl-skel-row">
            <div class="gl-skel" style="width:90px;height:12px;border-radius:6px"></div>
            <div style="flex:1.2">
              <div class="gl-skel" style="height:12px;width:80%;border-radius:6px"></div>
            </div>
            <div class="gl-skel" style="width:70px;height:20px;border-radius:20px"></div>
            <div class="gl-skel" style="width:72px;height:13px;border-radius:6px"></div>
            <div class="gl-skel" style="width:60px;height:20px;border-radius:20px"></div>
            <div class="gl-skel" style="width:80px;height:12px;border-radius:6px"></div>
            <div class="gl-skel" style="width:80px;height:12px;border-radius:6px"></div>
            <div class="gl-skel" style="width:64px;height:30px;border-radius:8px"></div>
          </div>
        </div>
      </template>

      <!-- Empty -->
      <div v-else-if="invoices.length === 0" class="gl-empty-msg">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="opacity:.3;margin-bottom:10px"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        <div>No invoices found</div>
      </div>

      <!-- Desktop table -->
      <div v-else class="gl-tbl-scroll">
        <table class="gl-tbl">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Company</th>
              <th>Plan</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Due Date</th>
              <th>Paid At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="invoice in invoices" :key="invoice.id">
              <td class="gl-td-mono">{{ invoice.invoice_number || '#' + invoice.id }}</td>
              <td>
                <router-link v-if="invoice.company" :to="`/super-admin/companies/${invoice.company.id}`" class="gl-link gl-td-bold">
                  {{ invoice.company.name }}
                </router-link>
                <span v-else class="gl-td-muted">—</span>
              </td>
              <td>
                <span v-if="invoice.plan" class="gl-badge gl-badge-plan">{{ invoice.plan.name }}</span>
                <span v-else class="gl-td-muted">—</span>
              </td>
              <td class="gl-td-bold">RM {{ formatCurrency(invoice.amount) }}</td>
              <td><span :class="getStatusBadge(invoice.status)">{{ invoice.status }}</span></td>
              <td class="gl-td-muted">{{ formatDate(invoice.due_date) }}</td>
              <td class="gl-td-muted">{{ invoice.paid_at ? formatDate(invoice.paid_at) : '—' }}</td>
              <td>
                <div class="gl-act-group">
                  <button class="gl-act-btn gl-act-view" @click="viewInvoice(invoice)" title="View">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  </button>
                  <button v-if="invoice.status === 'pending'" class="gl-act-btn gl-act-success" @click="markAsPaid(invoice)" title="Mark as Paid">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile cards -->
      <div v-if="!loading && invoices.length" class="gl-mob-list">
        <div v-for="invoice in invoices" :key="'m' + invoice.id" class="gl-mob-card">
          <div class="gl-mob-top">
            <div>
              <div class="gl-mob-inv-num">{{ invoice.invoice_number || '#' + invoice.id }}</div>
              <router-link v-if="invoice.company" :to="`/super-admin/companies/${invoice.company.id}`" class="gl-mob-name">
                {{ invoice.company.name }}
              </router-link>
            </div>
            <div style="display:flex;flex-direction:column;align-items:flex-end;gap:6px">
              <span :class="getStatusBadge(invoice.status)">{{ invoice.status }}</span>
              <span class="gl-mob-amount">RM {{ formatCurrency(invoice.amount) }}</span>
            </div>
          </div>
          <div class="gl-mob-bottom">
            <div class="gl-mob-kv">
              <span class="gl-mob-k">Plan</span>
              <span class="gl-mob-v">
                <span v-if="invoice.plan" class="gl-badge gl-badge-plan" style="font-size:10.5px">{{ invoice.plan.name }}</span>
                <span v-else style="color:#8a9e9a">—</span>
              </span>
            </div>
            <div class="gl-mob-kv">
              <span class="gl-mob-k">Due Date</span>
              <span class="gl-mob-v">{{ formatDate(invoice.due_date) }}</span>
            </div>
            <div class="gl-mob-kv">
              <span class="gl-mob-k">Paid At</span>
              <span class="gl-mob-v">{{ invoice.paid_at ? formatDate(invoice.paid_at) : '—' }}</span>
            </div>
          </div>
          <div v-if="invoice.status === 'pending'" class="gl-mob-actions">
            <button class="gl-btn-paid" @click="markAsPaid(invoice)">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Mark as Paid
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="!loading && meta.last_page > 1" class="gl-pager">
      <div class="gl-pager-info">Showing {{ (meta.current_page - 1) * meta.per_page + 1 }}–{{ Math.min(meta.current_page * meta.per_page, meta.total) }} of {{ meta.total }}</div>
      <div class="gl-pager-btns">
        <button class="gl-pager-btn" :disabled="meta.current_page === 1" @click="changePage(meta.current_page - 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <button v-for="page in paginationPages" :key="page" class="gl-pager-btn" :class="{ 'gl-pager-btn-active': page === meta.current_page }" @click="changePage(page)">{{ page }}</button>
        <button class="gl-pager-btn" :disabled="meta.current_page === meta.last_page" @click="changePage(meta.current_page + 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { superAdminSubscriptionApi } from '@/services/superAdminApi';

export default {
  name: 'InvoiceList',
  data() {
    return {
      loading: false,
      search: '',
      searchTimeout: null,
      filters: { status: '' },
      stats: null,
      invoices: [],
      meta: { current_page: 1, last_page: 1, per_page: 20, total: 0 }
    };
  },
  computed: {
    paginationPages() {
      const pages = [];
      const start = Math.max(1, this.meta.current_page - 2);
      const end = Math.min(this.meta.last_page, this.meta.current_page + 2);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  created() {
    this.fetchStats();
    this.fetchInvoices();
  },
  methods: {
    async fetchStats() {
      try {
        const response = await superAdminSubscriptionApi.getInvoiceStats();
        if (response.data.success) this.stats = response.data.data;
      } catch (error) {
        this.stats = { total_revenue: 0, this_month: 0, pending_count: 0, total_count: 0 };
      }
    },
    async fetchInvoices() {
      this.loading = true;
      try {
        const params = { page: this.meta.current_page, per_page: this.meta.per_page, search: this.search || undefined };
        if (this.filters.status) params.status = this.filters.status;
        const response = await superAdminSubscriptionApi.listInvoices(params);
        if (response.data.success) { this.invoices = response.data.data; this.meta = response.data.meta; }
      } catch (error) { this.invoices = []; }
      finally { this.loading = false; }
    },
    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => { this.meta.current_page = 1; this.fetchInvoices(); }, 300);
    },
    changePage(page) {
      if (page >= 1 && page <= this.meta.last_page) { this.meta.current_page = page; this.fetchInvoices(); }
    },
    viewInvoice(invoice) {
      alert('Invoice Details: #' + (invoice.invoice_number || invoice.id));
    },
    async markAsPaid(invoice) {
      if (!confirm('Mark this invoice as paid?')) return;
      try {
        const response = await superAdminSubscriptionApi.markInvoicePaid(invoice.id);
        if (response.data.success) { invoice.status = 'paid'; invoice.paid_at = new Date().toISOString(); this.fetchStats(); }
      } catch (error) { alert(error.response?.data?.message || 'Failed to mark as paid'); }
    },
    formatCurrency(value) {
      if (!value) return '0.00';
      return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    getStatusBadge(status) {
      return { paid: 'gl-badge gl-badge-active', pending: 'gl-badge gl-badge-pending', overdue: 'gl-badge gl-badge-inactive', cancelled: 'gl-badge gl-badge-muted' }[status] || 'gl-badge gl-badge-info';
    }
  }
};
</script>

<style scoped>
.gl-page { padding: 24px 28px 48px; }
.gl-page-header { margin-bottom: 20px; }
.gl-page-title { font-size: 22px; font-weight: 800; color: #1a2e2b; letter-spacing: -0.4px; margin: 0 0 4px; }
.gl-breadcrumb { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #8a9e9a; }
.gl-breadcrumb-link { color: #900C27; text-decoration: none; }
.gl-breadcrumb-link:hover { text-decoration: underline; }
.gl-breadcrumb-sep { opacity: .5; }

.gl-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 16px; }
.gl-stat-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; padding: 18px 20px; display: flex; align-items: center; gap: 14px; }
.gl-stat-icon { width: 42px; height: 42px; border-radius: 11px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-icon-teal  { background: rgba(144,12,39,.12); color: #900C27; }
.gl-icon-blue  { background: rgba(59,130,246,.12); color: #3b82f6; }
.gl-icon-green { background: rgba(22,163,74,.12);  color: #16a34a; }
.gl-icon-amber { background: rgba(245,158,11,.12); color: #d97706; }
.gl-stat-label { font-size: 11px; font-weight: 600; color: #8a9e9a; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 4px; }
.gl-stat-value { font-size: 20px; font-weight: 800; color: #1a2e2b; letter-spacing: -0.5px; margin-bottom: 2px; }
.gl-stat-sub { font-size: 11px; color: #8a9e9a; }

.gl-filter-bar { display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; }
.gl-search-wrap { position: relative; flex: 1; min-width: 180px; }
.gl-search-icon { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: #8a9e9a; pointer-events: none; }
.gl-search-input { width: 100%; padding: 9px 12px 9px 34px; border: 1px solid #e0e7e5; border-radius: 9px; font-size: 13px; color: #2a3d3a; background: #fff; outline: none; transition: border-color .15s; box-sizing: border-box; }
.gl-search-input:focus { border-color: #900C27; }
.gl-select-wrap { min-width: 140px; }
.gl-select { width: 100%; padding: 9px 12px; border: 1px solid #e0e7e5; border-radius: 9px; font-size: 13px; color: #2a3d3a; background: #fff; outline: none; cursor: pointer; }

.gl-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; overflow: hidden; margin-bottom: 16px; }
.gl-tbl-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.gl-tbl { width: 100%; border-collapse: collapse; min-width: 720px; }
.gl-tbl thead th { font-size: 11px; font-weight: 700; color: #8a9e9a; text-transform: uppercase; letter-spacing: .4px; padding: 11px 18px; background: #f8faf9; border-bottom: 1px solid #eef0ef; white-space: nowrap; }
.gl-tbl tbody tr { border-bottom: 1px solid #f5f7f6; transition: background .12s; }
.gl-tbl tbody tr:last-child { border-bottom: none; }
.gl-tbl tbody tr:hover { background: #f9fbfa; }
.gl-tbl tbody td { padding: 13px 18px; font-size: 13px; color: #2a3d3a; }
.gl-td-muted { color: #6b7f7b !important; }
.gl-td-bold { font-weight: 700; color: #1a2e2b !important; }
.gl-td-mono { font-family: 'Menlo','Consolas',monospace; font-size: 12px; color: #900C27 !important; font-weight: 600; }
.gl-link { color: #900C27; font-weight: 600; text-decoration: none; }
.gl-link:hover { text-decoration: underline; }

.gl-badge { display: inline-block; font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 20px; white-space: nowrap; text-transform: capitalize; }
.gl-badge-active   { background: #e0f0ec; color: #1a5248; }
.gl-badge-inactive { background: #fce7e7; color: #dc2626; }
.gl-badge-pending  { background: #fef3c7; color: #92400e; }
.gl-badge-info     { background: #dbeafe; color: #1d4ed8; }
.gl-badge-muted    { background: #f0f3f2; color: #6b7f7b; }
.gl-badge-plan     { background: rgba(144,12,39,.1); color: #1a5248; }

.gl-act-group { display: flex; gap: 6px; }
.gl-act-btn { width: 30px; height: 30px; border-radius: 7px; border: 1px solid; display: flex; align-items: center; justify-content: center; cursor: pointer; background: transparent; transition: all .15s; }
.gl-act-view { border-color: #900C27; color: #900C27; }
.gl-act-view:hover { background: rgba(144,12,39,.08); }
.gl-act-success { border-color: #16a34a; color: #16a34a; }
.gl-act-success:hover { background: rgba(22,163,74,.08); }

.gl-skel { background: linear-gradient(90deg, #f0f2f1 25%, #e5e8e7 50%, #f0f2f1 75%); background-size: 200% 100%; animation: gl-shimmer 1.4s infinite; border-radius: 6px; }
.gl-tbl-skel-head { display: flex; align-items: center; gap: 16px; padding: 12px 18px; background: #f8faf9; border-bottom: 1px solid #eef0ef; }
.gl-skel-row { display: flex; align-items: center; gap: 16px; padding: 14px 18px; border-bottom: 1px solid #f5f7f6; }
@keyframes gl-shimmer { to { background-position: -200% 0; } }

.gl-empty-msg { padding: 48px 0; text-align: center; font-size: 13px; color: #a0b5b0; display: flex; flex-direction: column; align-items: center; }

.gl-mob-list { display: none; }

.gl-pager { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; }
.gl-pager-info { font-size: 12.5px; color: #8a9e9a; }
.gl-pager-btns { display: flex; gap: 4px; }
.gl-pager-btn { min-width: 32px; height: 32px; border: 1px solid #e0e7e5; border-radius: 7px; background: #fff; color: #2a3d3a; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0 6px; transition: all .15s; }
.gl-pager-btn:hover:not(:disabled) { border-color: #900C27; color: #900C27; }
.gl-pager-btn:disabled { opacity: .4; cursor: not-allowed; }
.gl-pager-btn-active { background: #900C27 !important; border-color: #900C27 !important; color: #fff !important; font-weight: 700; }

@media (max-width: 767px) {
  .gl-page { padding: 14px 16px 40px; }
  .gl-page-header { margin-bottom: 14px; }
  .gl-breadcrumb { display: none; }
  .gl-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .gl-stat-card { padding: 14px; gap: 10px; }
  .gl-stat-icon { width: 36px; height: 36px; border-radius: 9px; }
  .gl-stat-value { font-size: 17px; }
  .gl-filter-bar { gap: 8px; }
  .gl-search-wrap { flex: 1 1 100%; min-width: 0; }
  .gl-select-wrap { flex: 1 1 100%; }

  .gl-tbl-scroll { display: none; }
  .gl-tbl-skel-head { display: none; }
  .gl-mob-list { display: flex; flex-direction: column; }
  .gl-skel-row { padding: 12px 16px; gap: 10px; }

  .gl-mob-card { padding: 14px 16px; border-bottom: 1px solid #f0f3f2; }
  .gl-mob-card:last-child { border-bottom: none; }
  .gl-mob-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 10px; }
  .gl-mob-inv-num { font-family: 'Menlo','Consolas',monospace; font-size: 12px; color: #900C27; font-weight: 600; margin-bottom: 3px; }
  .gl-mob-name { font-size: 14px; font-weight: 700; color: #1a2e2b; text-decoration: none; display: block; }
  .gl-mob-name:hover { text-decoration: underline; color: #900C27; }
  .gl-mob-amount { font-size: 15px; font-weight: 800; color: #1a2e2b; }
  .gl-mob-bottom { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 14px; padding-top: 10px; border-top: 1px solid #f5f7f6; margin-bottom: 10px; }
  .gl-mob-kv { display: flex; flex-direction: column; gap: 2px; }
  .gl-mob-k { font-size: 10.5px; font-weight: 600; color: #8a9e9a; text-transform: uppercase; letter-spacing: .3px; }
  .gl-mob-v { font-size: 13px; font-weight: 700; color: #1a2e2b; }
  .gl-mob-actions { }
  .gl-btn-paid { width: 100%; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 8px 12px; border: 1px solid #16a34a; border-radius: 8px; color: #16a34a; font-size: 13px; font-weight: 600; cursor: pointer; background: transparent; }
  .gl-btn-paid:hover { background: rgba(22,163,74,.06); }

  .gl-pager { flex-direction: column; align-items: flex-start; gap: 8px; }
}
</style>

<style>
body.dark-only .gl-page-title { color: #e0eded; }
body.dark-only .gl-breadcrumb-link { color: #5a7470; }
body.dark-only .gl-stat-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-stat-label { color: #5a7470; }
body.dark-only .gl-stat-value { color: #e0eded; }
body.dark-only .gl-stat-sub { color: #5a7470; }
body.dark-only .gl-search-input { background: #1e2d2b; border-color: #2a3d3a; color: #c0d4d0; }
body.dark-only .gl-search-input:focus { border-color: #3d9e8c; }
body.dark-only .gl-search-icon { color: #5a7470; }
body.dark-only .gl-select { background: #1e2d2b; border-color: #2a3d3a; color: #c0d4d0; }
body.dark-only .gl-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-tbl thead th { background: #16211f; color: #5a7470; border-bottom-color: #2a3d3a; }
body.dark-only .gl-tbl tbody tr { border-bottom-color: #222f2d; }
body.dark-only .gl-tbl tbody tr:hover { background: #1c2f2b; }
body.dark-only .gl-tbl tbody td { color: #c0d4d0; }
body.dark-only .gl-td-muted { color: #5a7470 !important; }
body.dark-only .gl-td-bold { color: #e0eded !important; }
body.dark-only .gl-skel { background: linear-gradient(90deg, #243432 25%, #2a3d3a 50%, #243432 75%); background-size: 200% 100%; }
body.dark-only .gl-tbl-skel-head { background: #16211f; border-bottom-color: #2a3d3a; }
body.dark-only .gl-skel-row { border-bottom-color: #222f2d; }
body.dark-only .gl-pager-btn { background: #1e2d2b; border-color: #2a3d3a; color: #c0d4d0; }
body.dark-only .gl-pager-info { color: #5a7470; }
body.dark-only .gl-mob-card { border-bottom-color: #222f2d; }
body.dark-only .gl-mob-inv-num { color: #3d9e8c; }
body.dark-only .gl-mob-name { color: #e0eded; }
body.dark-only .gl-mob-amount { color: #e0eded; }
body.dark-only .gl-mob-bottom { border-top-color: #2a3d3a; }
body.dark-only .gl-mob-v { color: #e0eded; }
body.dark-only .gl-empty-msg { color: #5a7470; }
</style>
